import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Megaphone } from "lucide-react";
import {
  AnnouncementListItem,
  AnnouncementListItemSkeleton,
} from "../components/announcements/AnnouncementListItem";
import { AnnouncementPageHeader } from "../components/announcements/AnnouncementPageHeader";
import { TablePaginationFooter } from "../components/data-table/TablePaginationFooter";
import { useAnnouncements } from "../hooks/useAnnouncements";
import { sortAnnouncements } from "@/utils/announcementSort";

const ANNOUNCEMENT_PAGE_SIZE = 10;

export function Announcements() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { announcements, total, loading, error } = useAnnouncements(
    page,
    ANNOUNCEMENT_PAGE_SIZE
  );
  const sorted = useMemo(
    () => sortAnnouncements(announcements),
    [announcements]
  );
  const totalPages = Math.max(1, Math.ceil(total / ANNOUNCEMENT_PAGE_SIZE));

  return (
    <div className="space-y-6">
      <AnnouncementPageHeader
        backTo="/"
        title={t("announcements.listTitle")}
        subtitle={t("announcements.listSubtitle")}
      />

      <div className="space-y-3">
        {loading && sorted.length === 0 ? (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <AnnouncementListItemSkeleton key={index} />
            ))}
          </>
        ) : null}

        {!loading && error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {t("announcements.loadFailed")}
          </div>
        ) : null}

        {!loading && !error && sorted.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 py-16 text-center dark:border-[#D2F0E1]/5 dark:bg-[#141517]">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 dark:border-slate-800 dark:bg-[#D2F0E1]/5">
              <Megaphone className="h-8 w-8" />
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white">
              {t("announcements.emptyTitle")}
            </h3>
          </div>
        ) : null}

        {sorted.map((item) => (
          <AnnouncementListItem key={item.id} item={item} />
        ))}
      </div>

      {sorted.length > 0 ? (
        <TablePaginationFooter
          page={page}
          totalPages={totalPages}
          totalItems={total}
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}
